import { useState } from 'react'
import { api } from '../../api'
import { useCachedFetch } from '../../lib/swr'
import { useActionsPortail } from '../../contexts/navigation'
import { partagerProducteur } from '../../lib/partage'
import { ouvrirModaleVisuel } from './ModaleVisuel'

/**
 * Carte « Votre page est prête à X % » — accueil producteur (S62).
 *
 * POURQUOI : la vision MVP (Daniel) est qu'un producteur soit FIER de sa page.
 * Le formulaire n'y suffit pas : il faut voir ce qui manque, en trois cases, et
 * une jauge qui monte. Chaque ligne mene directement a l'endroit ou l'on agit.
 *
 * Source : la fiche PUBLIQUE (/public/producteurs/:id) — exactement ce que le
 * client voit — plus l'organisation pour l'identifiant. Cache SWR partage avec
 * « Ma page » : aucun appel en double.
 */
const ETAPES = [
  { k: 'photo', l: 'Une photo de l’exploitation', e: '📷', ok: (f) => !!f.logo, tab: 'profilprod' },
  { k: 'texte', l: 'Quelques lignes sur vous', e: '✍️', ok: (f) => (f.description || '').trim().length >= 40, tab: 'profilprod' },
  { k: 'ville', l: 'Votre commune', e: '📍', ok: (f) => !!f.ville, tab: 'profilprod' },
  { k: 'produit', l: 'Un premier produit', e: '🥭', ok: (f) => (f.offers || []).length > 0, tab: 'offers' },
]

export default function CarteMaPage() {
  const { setHubTab } = useActionsPortail()
  const { data: org } = useCachedFetch('producer/me', api.producer.getMe)
  const { data: fiche } = useCachedFetch(
    org?.id ? `producteur/${org.id}` : null,
    () => api.getProducteur(org.id),
    { enabled: !!org?.id },
  )
  // Retour visible apres un partage (lien copie / echec), 2,8 s
  const [flash, setFlash] = useState(null)
  if (!fiche) return null

  const partager = async () => {
    const r = await partagerProducteur({ id: org.id, nom: fiche.nomCommercial || fiche.nom, description: fiche.description })
    const msg = r === 'copie' ? '✅ Lien copié !' : r === 'echec' ? 'Impossible de partager ici' : null
    if (msg) { setFlash(msg); setTimeout(() => setFlash(null), 2800) }
  }

  const faits = ETAPES.filter((e) => e.ok(fiche))
  const pct = Math.round((faits.length / ETAPES.length) * 100)
  const complet = pct === 100
  const manquantes = ETAPES.filter((e) => !e.ok(fiche))

  return (
    <div className="panneau-verre rounded-3xl p-4 mt-3 animate-fade-up" style={{ animationDelay: '0.08s' }}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.18em]" style={{ color: 'rgb(var(--c-primary))' }}>Ma page</p>
          <p className="font-serif text-[22px] leading-tight text-ink mt-0.5">
            {complet ? 'Votre page est prête ✨' : `Prête à ${pct} %`}
          </p>
        </div>
        <button
          onClick={() => setHubTab('profilprod')}
          className="flex-shrink-0 text-[11.5px] font-extrabold px-3.5 py-2 rounded-full text-green"
          style={{ background: 'rgb(var(--c-primary) / 0.85)' }}
        >
          Voir ma page ›
        </button>
      </div>

      {/* Jauge */}
      <div className="h-2 rounded-full mt-3 overflow-hidden" style={{ background: 'rgba(7,62,61,0.1)' }}>
        <div className="h-full rounded-full transition-[width] duration-700"
          style={{ width: `${pct}%`, background: 'linear-gradient(90deg, rgb(var(--c-primary)), rgb(var(--c-teal)))' }} />
      </div>

      {/* Ce qui reste a faire — une ligne par manque, chacune mene a l'endroit ou agir */}
      {!complet && (
        <div className="flex flex-col gap-1.5 mt-3">
          {manquantes.map((e) => (
            <button key={e.k} onClick={() => setHubTab(e.tab)}
              className="flex items-center gap-2.5 text-left rounded-2xl px-3 py-2.5 active:scale-[0.99] transition-transform"
              style={{ background: 'rgba(255,255,255,0.55)', border: '1px solid rgba(7,62,61,0.08)' }}>
              <span className="w-5 h-5 rounded-full flex-shrink-0" style={{ border: '2px solid rgba(7,62,61,0.25)' }} />
              <span className="text-[16px] leading-none">{e.e}</span>
              <span className="flex-1 text-[13px] font-semibold text-ink">{e.l}</span>
              <span className="text-[12px] text-ink/50">›</span>
            </button>
          ))}
        </div>
      )}
      {complet && (
        <>
          <p className="text-[12.5px] text-ink/65 mt-2.5 leading-snug">
            Photo, présentation, commune et produits : les clients voient tout. Bravo !
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => ouvrirModaleVisuel({ id: org.id })}
              className="flex-1 py-3 rounded-full text-[13.5px] font-extrabold text-green"
              style={{ background: 'rgb(var(--c-primary))' }}
            >
              🖼️ Créer un visuel
            </button>
            <button
              onClick={partager}
              className="flex-1 py-3 rounded-full text-[13.5px] font-bold text-green glass-surface"
            >
              {flash || '↗ Partager'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
